import { makeAutoObservable } from "mobx";
import { TRISTATE_CHECKBOX_VALUES } from "../../../components/inputs/tristate-checkbox/tristate-checkbox";
import { AVAILABLE_BETA_FEATURES_MAP, FEATURES, IPlanFeature, NOT_BETA_FEATURES_MAP } from "../../../models/Plan";
import FeatureStore, { IFeatureChanges } from "../../../stores/feature-store";
import SubscriptionPlanEditorStore, { PLAN_CHARGE_TYPES } from "./subscription-plan-editor-store";

export class FeatureListManager {
    private _featureList: FeatureStore[] = [];

    constructor(private _plan: SubscriptionPlanEditorStore, features?: IPlanFeature[]) {
        makeAutoObservable(this, {}, {autoBind: true});

        this._featureList = this._getFeaturesToBuildList.map((feature_id: FEATURES) => {
            const featureModel = features?.find(f => f.feature_id === feature_id);
            if (featureModel) {
                return new FeatureStore(featureModel.feature_id, {...featureModel, _isEnabled: true});
            }
            return new FeatureStore(feature_id);
        });

        if (!this._isAddonsAllowed) {
            const addonsFeature = this._featureList.find(i => i.feature_id === FEATURES.ADDONS);
            addonsFeature?.setDisabled(true);
        }
    }

    private get _getFeaturesToBuildList(): number[] {
        return [...Object.keys(NOT_BETA_FEATURES_MAP), ...this.availableBetaFeatures].map(id => parseInt(id));
    }

    private get _isAddonsAllowed(): boolean {
        return this._plan.type === PLAN_CHARGE_TYPES.RECURRING;
    }

    public setChanges(features?: IFeatureChanges[]): void {
        this._featureList = this._getFeaturesToBuildList.map((feature_id: FEATURES) => {
            const featureChanges = features?.find(f => f.feature_id === feature_id);
            return (new FeatureStore(feature_id)).setChanges({
                feature_id,
                _isEnabled: featureChanges ? true : false,
                ...featureChanges
            });
        });
        this.handlePlanChanges();
    }

    public discardChanges(): void {
        this._featureList.forEach(featStore => featStore.discardChanges());
    }

    public hasChanges(): boolean {
        return this._featureList.some(s => s.hasChanges());
    }

    public getUpdatedModel(): IPlanFeature[] {
        return this._featureList.filter(f => f.isEnabled).map(f => f.getUpdatedModel());
    }

    public getFeature(featureId: number): FeatureStore | undefined {
        return this._featureList.find(i => i.feature_id === featureId);
    }

    public getFeaturesCount(): number {
        return this._featureList.filter(f => f.isEnabled).length;
    }

    public getFeaturesInfo(): string {
        return this._featureList.filter(f => f.isEnabled).map(f => f.name).join(', ');
    }

    /** this method should be called every time something changes on the parent store that might affect feature stores */
    public handlePlanChanges(): void {
        const addonsFeature = this._featureList.find(i => i.feature_id === FEATURES.ADDONS);
        addonsFeature?.setDisabled(!this._isAddonsAllowed);
        if (!this._isAddonsAllowed) {
            addonsFeature?.setIsEnabled(false);
        }
    }

    public toggleFeature(featureId: number, isAdd: boolean) {
        this.getFeature(featureId)?.setIsEnabled(isAdd);
    }

    public onToggleAllFeatures(v: TRISTATE_CHECKBOX_VALUES): void {
        if (v === TRISTATE_CHECKBOX_VALUES.CHECKED) {
            this._featureList.forEach(f => {
                if (f.feature_id === FEATURES.ADDONS && !this._isAddonsAllowed) {
                    return;
                }
                f.setIsEnabled(true);
            });
        }
        if (v === TRISTATE_CHECKBOX_VALUES.UNCHECKED) {
            this._featureList.forEach(f => f.setIsEnabled(false));
        }
    }

    public getAllFeaturesToggleState(): TRISTATE_CHECKBOX_VALUES {

        const areAllEnabled = this._featureList.every(feat => {
            if (feat.feature_id === FEATURES.ADDONS && !this._isAddonsAllowed) {
                /** when addons checkbox is disabled - we want the tristate checkbox still show as if all checkboxes are checked */
                return true;
            }        
            return feat.isEnabled;
        });


        if (areAllEnabled) {
            return TRISTATE_CHECKBOX_VALUES.CHECKED;
        }
        if (this._featureList.some(feat => feat.isEnabled)) {
            return TRISTATE_CHECKBOX_VALUES.PARTIALLY_CHECKED;
        }
        return TRISTATE_CHECKBOX_VALUES.UNCHECKED;
    }

    public get availableBetaFeatures(): string[] {
        return Object.keys(AVAILABLE_BETA_FEATURES_MAP);
    }
    

}